/**
 * SurveyPage — public `/survey/:token`. No login required.
 * Step 1: Overall rating (1–5) + NPS + would-attend-again + comment
 * Step 2: Build your event profile (preferences)
 * Step 3: Thank you
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Star, Loader2, CheckCircle2, Heart, ArrowRight, ArrowLeft, Music, Calendar,
  MapPin, Users, DollarSign, Bell, Utensils, ThumbsDown,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPE_OPTIONS = [
  'Wine tasting', 'Live music', 'Comedy', 'Food festival', 'Cocktail party',
  'Dance / nightlife', 'Theater', 'Art show', 'Sports', 'Workshop / class',
  'Outdoor / adventure', 'Conference', 'Networking', 'Brunch', 'Family-friendly',
];
const MUSIC_GENRE_OPTIONS = [
  'Jazz', 'Hip hop', 'R&B / Soul', 'EDM / House', 'Rock', 'Country',
  'Latin', 'Indie / Alt', 'Classical', 'Pop', 'Funk', 'Reggae',
];
const DAY_OPTIONS = [
  { value: 'thu', label: 'Thu' }, { value: 'fri', label: 'Fri' },
  { value: 'sat', label: 'Sat' }, { value: 'sun', label: 'Sun' },
  { value: 'mon', label: 'Mon' }, { value: 'tue', label: 'Tue' }, { value: 'wed', label: 'Wed' },
];
const COMPANION_OPTIONS = [
  { value: 'solo', label: 'Solo' },
  { value: 'date_night', label: 'Date night' },
  { value: 'group', label: 'Group of friends' },
];
const DIETARY_OPTIONS = ['Vegetarian','Vegan','Gluten-free','Halal','Kosher','Dairy-free'];
const FREQ_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'off',     label: 'Don\u2019t email me' },
];

function ChipPicker({ options, value, onChange, testid }) {
  const isObj = options.length > 0 && typeof options[0] === 'object';
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((opt) => {
        const v = isObj ? opt.value : opt;
        const label = isObj ? opt.label : opt;
        const selected = (value || []).includes(v);
        return (
          <button
            key={v}
            type="button"
            onClick={() => {
              const cur = new Set(value || []);
              if (selected) cur.delete(v); else cur.add(v);
              onChange([...cur]);
            }}
            data-testid={`${testid}-${v}`}
            className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
              selected
                ? 'bg-indigo-600 text-white border border-indigo-600'
                : 'bg-card text-foreground border border-border hover:border-indigo-700/60'
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function StarRating({ value, onChange, testid }) {
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          data-testid={`${testid}-${n}`}
          className="transition-transform hover:scale-110 focus:outline-none"
        >
          <Star
            className={`h-9 w-9 ${n <= (value || 0)
              ? 'fill-amber-300 text-amber-300'
              : 'text-muted-foreground/40'}`}
          />
        </button>
      ))}
    </div>
  );
}

export default function SurveyPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const dismiss = searchParams.get('dismiss') === '1';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Step 1 — rating
  const [overall, setOverall] = useState(0);
  const [nps, setNps] = useState(null);
  const [wouldAttendAgain, setWouldAttendAgain] = useState(null);
  const [comments, setComments] = useState('');

  // Step 2 — preferences
  const [eventTypes, setEventTypes] = useState([]);
  const [musicGenres, setMusicGenres] = useState([]);
  const [preferredDays, setPreferredDays] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [companions, setCompanions] = useState('');
  const [dietary, setDietary] = useState([]);
  const [dislikes, setDislikes] = useState('');
  const [notifFrequency, setNotifFrequency] = useState('biweekly');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/surveys/by-token/${token}`);
      setData(res.data);
      if (res.data?.survey?.status === 'completed') setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Survey not found');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!overall) { toast.error('Please rate the event first'); return; }
    setSubmitting(true);
    try {
      const neighborhoodsArr = neighborhoods
        .split(',').map((s) => s.trim()).filter(Boolean);
      const dislikesArr = dislikes
        .split(',').map((s) => s.trim()).filter(Boolean);
      await axios.post(`${API}/surveys/by-token/${token}`, {
        overall_rating: overall,
        nps,
        would_attend_again: wouldAttendAgain,
        comments,
        event_types: eventTypes,
        music_genres: musicGenres,
        preferred_days: preferredDays,
        preferred_neighborhoods: neighborhoodsArr,
        price_max: priceMax ? parseFloat(priceMax) : null,
        companions: companions || null,
        dietary,
        dislikes: dislikesArr,
        notification_frequency: notifFrequency,
      });
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-7 w-7 animate-spin text-indigo-400" />
      </div>
    );
  }
  if (!data?.survey) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="bg-card border-border max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <h2 className="text-xl font-semibold text-foreground">Survey not found</h2>
            <p className="text-sm text-muted-foreground mt-2">This link may have expired.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const event = data.event || {};

  if (done || dismiss) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="bg-gradient-to-br from-emerald-950/40 to-card border-emerald-700/40 max-w-md w-full" data-testid="survey-thank-you">
          <CardContent className="p-8 text-center">
            <div className="h-14 w-14 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-7 w-7 text-emerald-300" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">{dismiss ? "Got it — we'll stop emailing." : 'Thank you!'}</h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto">
              {dismiss
                ? "You won't receive any more post-event surveys."
                : "We'll use your taste to send you only the events you'll actually want to attend."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-16" data-testid="survey-page">
      {/* HERO */}
      <div
        className="px-4 py-12 sm:py-16 border-b border-border"
        style={event.image_url ? {
          backgroundImage: `linear-gradient(rgba(10,10,20,0.85), rgba(10,10,20,0.95)), url(${event.image_url})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
        } : { background: 'linear-gradient(135deg,#312e81,#0f172a)' }}
      >
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-xs uppercase tracking-wider font-semibold text-amber-300/90">Post-event survey · {step} of 2</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mt-2" data-testid="survey-event-name">
            How was {event.name || 'the event'}?
          </h1>
          <p className="text-sm text-foreground/70 mt-2">
            60 seconds. Help us send you more events you'll love.
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 mt-8 space-y-5">
        {step === 1 && (
          <Card className="bg-card border-border" data-testid="survey-step-1">
            <CardContent className="p-6 space-y-6">
              {/* Overall rating */}
              <div>
                <Label className="text-sm text-foreground font-semibold">Overall rating</Label>
                <p className="text-xs text-muted-foreground mb-3">How was your experience, top to bottom?</p>
                <StarRating value={overall} onChange={setOverall} testid="rating-overall" />
              </div>

              {/* NPS */}
              <div>
                <Label className="text-sm text-foreground font-semibold">
                  How likely are you to recommend this event to a friend?
                </Label>
                <p className="text-xs text-muted-foreground mb-3">0 = not at all · 10 = absolutely</p>
                <div className="grid grid-cols-11 gap-1" data-testid="nps-picker">
                  {[0,1,2,3,4,5,6,7,8,9,10].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setNps(n)}
                      data-testid={`nps-${n}`}
                      className={`py-2 rounded-md text-sm transition-colors ${
                        nps === n
                          ? 'bg-indigo-600 text-white'
                          : n <= 6 ? 'bg-rose-500/10 text-rose-300 hover:bg-rose-500/20'
                            : n <= 8 ? 'bg-amber-500/10 text-amber-300 hover:bg-amber-500/20'
                              : 'bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              {/* Would attend again */}
              <div>
                <Label className="text-sm text-foreground font-semibold">Would you attend this event again?</Label>
                <div className="flex gap-2 mt-3" data-testid="would-attend-picker">
                  {[
                    { v: true, label: 'Yes' },
                    { v: null, label: 'Maybe' },
                    { v: false, label: 'No' },
                  ].map((o) => (
                    <button
                      key={String(o.v)}
                      type="button"
                      onClick={() => setWouldAttendAgain(o.v)}
                      data-testid={`would-attend-${o.v === true ? 'yes' : o.v === false ? 'no' : 'maybe'}`}
                      className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                        wouldAttendAgain === o.v
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-card text-foreground border-border hover:border-indigo-700/60'
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Comments */}
              <div>
                <Label className="text-sm text-foreground font-semibold">Anything you'd like the organizer to know?</Label>
                <p className="text-xs text-muted-foreground mb-2">Optional — they really do read these.</p>
                <Textarea
                  rows={3}
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder="What you loved, what could be better..."
                  data-testid="survey-comments"
                />
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  disabled={!overall}
                  onClick={() => setStep(2)}
                  data-testid="survey-next"
                >
                  Next: your taste <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <Card className="bg-card border-border" data-testid="survey-step-2">
            <CardContent className="p-6 space-y-7">
              <div className="text-center">
                <Heart className="h-7 w-7 text-pink-400 mx-auto" />
                <h2 className="text-xl font-bold text-foreground mt-2">Build your event profile</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Pick anything that fits — we use this to send only events you'll love.
                </p>
              </div>

              <div>
                <Label className="text-sm text-foreground font-semibold flex items-center gap-2">
                  <Calendar className="h-3.5 w-3.5" /> Event types you enjoy
                </Label>
                <p className="text-xs text-muted-foreground mb-2">Pick as many as you like.</p>
                <ChipPicker options={EVENT_TYPE_OPTIONS} value={eventTypes} onChange={setEventTypes} testid="picker-event-types" />
              </div>

              <div>
                <Label className="text-sm text-foreground font-semibold flex items-center gap-2">
                  <Music className="h-3.5 w-3.5" /> Music genres you love
                </Label>
                <ChipPicker options={MUSIC_GENRE_OPTIONS} value={musicGenres} onChange={setMusicGenres} testid="picker-music" />
              </div>

              <div>
                <Label className="text-sm text-foreground font-semibold flex items-center gap-2">
                  <Calendar className="h-3.5 w-3.5" /> Days you usually go out
                </Label>
                <ChipPicker options={DAY_OPTIONS} value={preferredDays} onChange={setPreferredDays} testid="picker-days" />
              </div>

              <div>
                <Label className="text-sm text-foreground font-semibold flex items-center gap-2">
                  <Users className="h-3.5 w-3.5" /> Who do you usually go with?
                </Label>
                <ChipPicker options={COMPANION_OPTIONS} value={companions ? [companions] : []} onChange={(arr) => setCompanions(arr[arr.length - 1] || '')} testid="picker-companions" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-foreground font-semibold flex items-center gap-2">
                    <MapPin className="h-3.5 w-3.5" /> Neighborhoods you'd travel to
                  </Label>
                  <Input
                    placeholder="LoDo, RiNo, Cherry Creek..."
                    value={neighborhoods}
                    onChange={(e) => setNeighborhoods(e.target.value)}
                    className="mt-1"
                    data-testid="input-neighborhoods"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">Comma-separated.</p>
                </div>
                <div>
                  <Label className="text-sm text-foreground font-semibold flex items-center gap-2">
                    <DollarSign className="h-3.5 w-3.5" /> Most you'd pay for a ticket
                  </Label>
                  <Input
                    type="number"
                    placeholder="75"
                    value={priceMax}
                    onChange={(e) => setPriceMax(e.target.value)}
                    className="mt-1"
                    data-testid="input-price-max"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm text-foreground font-semibold flex items-center gap-2">
                  <Utensils className="h-3.5 w-3.5" /> Dietary needs
                </Label>
                <ChipPicker options={DIETARY_OPTIONS} value={dietary} onChange={setDietary} testid="picker-dietary" />
              </div>

              <div>
                <Label className="text-sm text-foreground font-semibold flex items-center gap-2">
                  <ThumbsDown className="h-3.5 w-3.5" /> What you'd rather skip
                </Label>
                <Input
                  placeholder="crowded clubs, EDM, parking hassles..."
                  value={dislikes}
                  onChange={(e) => setDislikes(e.target.value)}
                  className="mt-1"
                  data-testid="input-dislikes"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Comma-separated. We'll avoid these.</p>
              </div>

              <div>
                <Label className="text-sm text-foreground font-semibold flex items-center gap-2">
                  <Bell className="h-3.5 w-3.5" /> How often should we email recommendations?
                </Label>
                <ChipPicker
                  options={FREQ_OPTIONS}
                  value={notifFrequency ? [notifFrequency] : []}
                  onChange={(arr) => setNotifFrequency(arr[arr.length - 1] || '')}
                  testid="picker-frequency"
                />
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-border">
                <Button variant="ghost" onClick={() => setStep(1)} data-testid="survey-back">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back
                </Button>
                <Button
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  onClick={submit}
                  disabled={submitting}
                  data-testid="survey-submit"
                >
                  {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                  Submit
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
